import { useState, useCallback, useEffect, useRef } from "react";
import Sidebar from "../Sidebar";
import { T, S, fmtDate, DARK_THEME_CSS } from "./theme";
import { Toast } from "./components";
import Mascot from "./Mascot";
import Dashboard from "./Dashboard";
import Reports from "./Reports";
import Outreach from "./Outreach";
import HelpDesk from "./HelpDesk";
import StarRatings from "./StarRatings";
import Admin from "./Admin";
import LicenseGoal from "./LicenseGoal";

const Icon = ({ d, d2, circle, poly, points, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {d && <path d={d} />}{d2 && <path d={d2} />}
    {circle && <circle cx={circle[0]} cy={circle[1]} r={circle[2]} />}
    {poly && <polyline points={poly} />}
    {points && <polygon points={points} />}
  </svg>
);

const NAV_ITEMS = [
  {
    key: "dashboard", label: "License Tracker",
    icon: <Icon d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 2v2" d2="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" circle={[9,7,4]} />,
    children: [
      { key: "dashboard", label: "License Tracker" },
      { key: "license-goal", label: "License Goal" },
    ],
  },
  {
    key: "support", label: "Help Desk",
    icon: <Icon d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />,
  },
  {
    key: "ratings", label: "Star Ratings",
    icon: <Icon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
  },
  {
    key: "reports", label: "Reports",
    icon: <Icon d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" d2="M14 2v6h6M16 13H8M16 17H8M10 9H8" />,
  },
  {
    key: "outreach", label: "Outreach",
    icon: <Icon d="M22 2L11 13" d2="M22 2l-7 20-4-9-9-4z" />,
  },
];

function NavDropdown({ label, items, page, tabStyle, hoverHandlers, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ ...tabStyle, display: "flex", alignItems: "center", gap: 4 }}
        {...hoverHandlers}
      >
        {label}
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute", top: "100%", right: 0, marginTop: 4, minWidth: 140, zIndex: 50,
            background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
            boxShadow: T.shadow, padding: 4,
          }}
        >
          {items.map((item) => {
            const selected = item.key === page;
            return (
              <button
                key={item.key}
                role="menuitem"
                onClick={() => { onSelect(item.key); setOpen(false); }}
                style={{
                  display: "block", width: "100%", textAlign: "left", fontSize: 11, padding: "6px 10px",
                  borderRadius: 4, border: "none", cursor: "pointer", fontFamily: T.font, whiteSpace: "nowrap",
                  color: selected ? T.accent : T.textMuted, fontWeight: selected ? 600 : 400,
                  background: selected ? T.accentSoft : "transparent",
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function KarolineLicenseUtilization({ setAuth, setUserOrg }) {
  const VALID_PAGES = new Set(NAV_ITEMS.flatMap((n) => (n.children ? n.children.map((c) => c.key) : [n.key])));
  // Only the first /-segment is the page key — e.g. #outreach/123 or
  // #outreach?priority=null still resolves to "outreach"; Outreach.js itself
  // reads the full hash to decide list vs. detail view.
  const hashPage = window.location.hash.replace("#", "").split("?")[0].split("/")[0];
  const [page, setPage] = useState(VALID_PAGES.has(hashPage) ? hashPage : "dashboard");
  const [navOpen, setNavOpen] = useState(false);
  const [popEmoji, setPopEmoji] = useState(null); // "cat" | "heart" | null
  // Lifted up (rather than owned by Dashboard) so Thunder's header/mascot
  // easter eggs can open these panels from any page, not just while
  // Dashboard itself is mounted.
  const [userJourneyModalOpen, setUserJourneyModalOpen] = useState(false);
  const [stationGroupingModalOpen, setStationGroupingModalOpen] = useState(false);
  const [themeMode, setThemeMode] = useState(() => {
    try { return localStorage.getItem("karoline_theme") || "light"; } catch { return "light"; }
  });
  const toggleTheme = () => {
    setThemeMode((m) => {
      const next = m === "light" ? "dark" : "light";
      try { localStorage.setItem("karoline_theme", next); } catch {}
      return next;
    });
  };

  const goToPage = (key) => {
    setPage(key);
    window.location.hash = key;
  };

  const triggerEmoji = (type) => {
    setPopEmoji(type);
    setTimeout(() => setPopEmoji(null), 1200);
  };

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Orbitron:wght@700&display=swap";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);
  const [toast, setToast] = useState({ msg: "", visible: false });

  // Same "data refreshed" cadence shown on each summary card — prior day's
  // close-of-business, refreshed once daily.
  const dataAsOf = new Date();
  dataAsOf.setUTCDate(dataAsOf.getUTCDate() - 1);
  const dataRefreshedLabel = dataAsOf.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const showToast = useCallback((msg) => {
    setToast({ msg, visible: true });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  }, []);

  return (
    <div className="flex" data-karoline-theme={themeMode}>
      <style>{DARK_THEME_CSS}</style>
      <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
      <div className="flex-1" style={{ marginLeft: "var(--sidebar-width, 250px)" }}>
        <div style={S.layout}>
          {/* Top nav */}
          {/* background: T.bg (not T.surface) so the header blends into the
              page instead of reading as a distinct bar — in light mode these
              are the same color anyway, but in dark mode T.surface is a
              lighter shade than T.bg and the mismatch showed up as an
              unwanted divider line where the header met the page content. */}
          <header style={{ background: T.bg, flexShrink: 0, padding: "30px 28px 12px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {/* Left: stacked brand — KAROLINE over THUNDERCAT, staggered */}
            <div style={{ display: "flex", flexDirection: "column", gap: 2, lineHeight: 1, position: "relative" }}>
              {/* Floating emoji pop — floats downward below the brand */}
              <div style={{
                position: "absolute", bottom: -36, left: "50%",
                fontSize: 22, pointerEvents: "none", zIndex: 100,
                animation: popEmoji ? "emojiPopDown 1.2s ease forwards" : "none",
                display: popEmoji ? "block" : "none",
              }}>
                {popEmoji === "heart" ? "💜" : popEmoji === "cat" ? "🐱" : ""}
              </div>

              {/* KAROLINE — shifted right, clickable */}
              <span
                onClick={() => triggerEmoji("heart")}
                style={{
                  fontFamily: "'Orbitron', 'DM Sans', sans-serif",
                  fontSize: 18, fontWeight: 700, letterSpacing: 6, color: T.accent,
                  textTransform: "uppercase", paddingLeft: 18, cursor: "pointer",
                  textShadow: "0 0 12px rgba(124,58,237,0.5), 0 0 28px rgba(124,58,237,0.2)",
                  userSelect: "none",
                }}>Karoline</span>

              {/* THUNDERCAT — shifted left, amber, clickable */}
              <div
                onClick={() => triggerEmoji("cat")}
                style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}
              >
                <span style={{ fontSize: 13, filter: "drop-shadow(0 0 5px #e8920a)" }}>⚡</span>
                <span style={{
                  fontFamily: "'Orbitron', 'DM Sans', sans-serif",
                  fontSize: 13, fontWeight: 700, letterSpacing: 4, color: "#e8920a",
                  textTransform: "uppercase",
                  textShadow: "0 0 10px rgba(232,146,10,0.7), 0 0 24px rgba(232,146,10,0.3)",
                  userSelect: "none",
                }}>ThunderCats</span>
                <span style={{ fontSize: 13, filter: "drop-shadow(0 0 5px #e8920a)" }}>⚡</span>
              </div>

              <style>{`
                @keyframes emojiPopDown {
                  0%   { opacity: 0; transform: translateX(-50%) translateY(0) scale(0.5); }
                  20%  { opacity: 1; transform: translateX(-50%) translateY(6px) scale(1.3); }
                  60%  { opacity: 1; transform: translateX(-50%) translateY(14px) scale(1); }
                  100% { opacity: 0; transform: translateX(-50%) translateY(24px) scale(0.9); }
                }
              `}</style>
            </div>

            {/* Right: VA text + nav tabs on same row, bottom-aligned */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "flex-end", height: "100%", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {/* Nav tabs */}
                <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {NAV_ITEMS.map(({ key, label, children }) => {
                    const active = children ? children.some((c) => c.key === page) : page === key;
                    const tabStyle = {
                      fontSize: 11, padding: "3px 10px", borderRadius: 6, cursor: "pointer",
                      border: "none", fontFamily: T.font, whiteSpace: "nowrap",
                      color: active ? T.accent : T.textDim,
                      fontWeight: active ? 600 : 400,
                      background: active ? T.accentSoft : "transparent",
                      transition: "all 0.15s",
                    };
                    const hoverHandlers = {
                      onMouseEnter: (e) => { if (!active) e.currentTarget.style.color = T.textMuted; },
                      onMouseLeave: (e) => { if (!active) e.currentTarget.style.color = T.textDim; },
                    };
                    if (children) {
                      return (
                        <NavDropdown
                          key={key}
                          label={label}
                          items={children}
                          page={page}
                          tabStyle={tabStyle}
                          hoverHandlers={hoverHandlers}
                          onSelect={goToPage}
                        />
                      );
                    }
                    return (
                      <button key={key} onClick={() => goToPage(key)} style={tabStyle} {...hoverHandlers}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ width: 1, height: 12, background: T.border }} />
                <button
                  onClick={toggleTheme}
                  title={themeMode === "light" ? "Switch to dark mode" : "Switch to light mode"}
                  style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: T.textDim, fontSize: 11, fontFamily: T.font, padding: 0 }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = T.textMuted; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = T.textDim; }}
                >
                  {themeMode === "light" ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                  )}
                  {themeMode === "light" ? "Dark" : "Light"}
                </button>
              </div>
              {/* Data Refreshed — moved under the Dark/Light toggle so it doesn't
                  compete with the nav tabs on the main row. */}
              <div style={{ fontSize: 11, color: T.textDim }}>
                <span style={{ fontWeight: 600, color: T.textMuted }}>Refreshed:</span> {dataRefreshedLabel}
              </div>
            </div>
          </header>

          {/* Page content */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={S.content}>
              <div style={S.contentInner}>
                {page === "dashboard" && <Dashboard showToast={showToast} navigate={setPage} userJourneyModalOpen={userJourneyModalOpen} setUserJourneyModalOpen={setUserJourneyModalOpen} stationGroupingModalOpen={stationGroupingModalOpen} setStationGroupingModalOpen={setStationGroupingModalOpen} />}
                {page === "license-goal" && <LicenseGoal showToast={showToast} />}
                {page === "support" && <HelpDesk showToast={showToast} />}
                {page === "ratings" && <StarRatings showToast={showToast} />}
                {page === "reports" && <Reports showToast={showToast} />}
                {page === "outreach" && <Outreach showToast={showToast} />}
                {page === "admin" && <Admin showToast={showToast} />}
              </div>
            </div>
          </div>

          <Toast message={toast.msg} visible={toast.visible} />
          <Mascot
            onShowProviderLifecycle={() => {
              setPage("dashboard");
              window.location.hash = "dashboard";
              setUserJourneyModalOpen(true);
            }}
            onExplainStationGrouping={() => {
              setPage("dashboard");
              window.location.hash = "dashboard";
              setStationGroupingModalOpen(true);
            }}
          />
        </div>
      </div>
    </div>
  );
}
